import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Bell, Download, Search, Clock, FileText, Megaphone } from 'lucide-react';
import { cn } from '../lib/utils';
import { StatusBadge } from '../components/ui/StatusBadge';

const NoticeBoard: React.FC = () => {
    const [notices, setNotices] = useState<any[]>([]);
    const [filter, setFilter] = useState<'all' | 'urgent' | 'mom'>('all');
    const [search, setSearch] = useState('');

    useEffect(() => {
        api.get('/notices').then(res => setNotices(res.data));
    }, []);

    const filteredNotices = notices.filter(n => {
        const matchesSearch = n.title.toLowerCase().includes(search.toLowerCase()) ||
                             n.content?.toLowerCase().includes(search.toLowerCase());
        if (filter === 'urgent') return matchesSearch && n.title.toLowerCase().includes('urgent');
        if (filter === 'mom') return matchesSearch && n.is_mom;
        return matchesSearch;
    });

    return (
        <div className="space-y-8 max-w-3xl mx-auto">
            <div className="flex flex-col gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">Notice Board</h1>
                    <p className="text-slate-500 mt-1">Official updates and announcements for residents</p>
                </div>

                <div className="flex bg-slate-100 p-1 rounded-lg w-fit">
                    {(['all', 'urgent', 'mom'] as const).map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={cn(
                                "px-4 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider transition-all",
                                filter === f ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                            )}
                        >
                            {f}
                        </button>
                    ))}
                </div>
            </div>

            <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                    type="text"
                    placeholder="Search notices..."
                    className="w-full bg-white border border-slate-200 rounded-xl py-3 pl-12 pr-4 shadow-sm focus:ring-2 focus:ring-violet-500 outline-none transition-all"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </div>

            <div className="space-y-6">
                {filteredNotices.length > 0 ? filteredNotices.map((n: any) => (
                    <div key={n.id} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 hover:border-violet-200 transition-all group">
                        <div className="flex items-center gap-3 mb-4">
                            <span className={cn(
                                "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border",
                                n.is_mom ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-blue-50 text-blue-700 border-blue-100"
                            )}>
                                {n.is_mom ? 'Meeting Minutes' : 'Announcement'}
                            </span>
                            <span className="text-[10px] text-slate-400 font-bold uppercase flex items-center gap-1">
                                <Clock size={12} /> {new Date(n.created_at).toLocaleDateString()}
                            </span>
                        </div>

                        <h2 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-violet-600 transition-colors">{n.title}</h2>
                        <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap mb-6">{n.content}</p>

                        <div className="flex items-center justify-between pt-6 border-t border-slate-50">
                            <div className="flex items-center gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                <Megaphone size={14} className="text-violet-400" />
                                Posted by Management
                            </div>
                            {n.attachment_url && (
                                <a
                                    href={n.attachment_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center gap-2 text-violet-600 hover:text-violet-700 font-bold text-sm"
                                >
                                    <Download size={16} />
                                    <span>Download Attachment</span>
                                </a>
                            )}
                        </div>
                    </div>
                )) : (
                    <div className="py-20 text-center bg-white rounded-2xl border border-dashed border-slate-200">
                        <Bell size={40} className="text-slate-200 mx-auto mb-3" />
                        <p className="text-slate-500 font-medium">No notices found in this category</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NoticeBoard;
